import React, { useEffect, useState, useCallback } from 'react';
import {
  Container, Card, Table, Button, Form, Row, Col, Badge, Spinner, Alert, Modal
} from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../layouts/Header';
import axiosInstance from '../services/axiosInterceptor';
import ThaiDateInput from '../components/ThaiDateInput'

const STATUS = {
  unpaid: { label: 'ค้างชำระ', bg: 'danger' },
  partial: { label: 'ชำระบางส่วน', bg: 'warning' },
  paid: { label: 'ชำระครบ', bg: 'success' },
  cancelled: { label: 'ยกเลิก', bg: 'secondary' }
};
const STREAM = { general: 'สามัญ', religious: 'ศาสนา', common: 'ทุกคน' };
const METHOD = { cash: 'เงินสด', transfer: 'โอน' };
const INST_STATUS = {
  unpaid: { label: 'ยังไม่ชำระ', bg: 'secondary' },
  partial: { label: 'ชำระบางส่วน', bg: 'warning' },
  paid: { label: 'ชำระแล้ว', bg: 'success' }
};
const baht = (n) => Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 });
const thaiDate = (v) => (v ? new Date(v).toLocaleDateString('th-TH') : '-');

const FeeInvoiceDetail = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPay, setShowPay] = useState(false);
  const [saving, setSaving] = useState(false);
  const [payForm, setPayForm] = useState({ pay_amount: '', pay_date: '', pay_method: 'cash', receipt_no: '', payment_note: '' });
  const [showInst, setShowInst] = useState(false);
  const [instForm, setInstForm] = useState({ installment_count: 1, installment_start: '' });

  const fetchInvoice = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axiosInstance.get(`/api/fee/invoices/${invoiceId}`);
      if (res.data.success) setInvoice(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดใบแจ้งหนี้ไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  const openPay = () => {
    setPayForm({
      pay_amount: invoice ? invoice.balance : '',
      pay_date: new Date().toISOString().substring(0, 10),
      pay_method: 'cash',
      receipt_no: '',
      payment_note: ''
    });
    setShowPay(true);
  };

  const handlePay = async () => {
    const amount = Number(payForm.pay_amount);
    if (!amount || amount <= 0) { setError('กรุณากรอกจำนวนเงินที่ถูกต้อง'); return; }
    setSaving(true);
    setError('');
    try {
      await axiosInstance.post(`/api/fee/invoices/${invoiceId}/payments`, payForm);
      setShowPay(false);
      fetchInvoice();
    } catch (err) {
      setError(err.response?.data?.message || 'บันทึกการชำระไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const openInst = () => {
    const rows = invoice?.installments || [];
    const first = rows[0];
    setInstForm({
      installment_count: rows.length || 1,
      installment_start: first && first.due_date
        ? new Date(first.due_date).toLocaleDateString('sv-SE')  // YYYY-MM-DD ตามเวลาท้องถิ่น
        : ''
    });
    setShowInst(true);
  };

  const handleInst = async () => {
    setSaving(true);
    setError('');
    try {
      await axiosInstance.post(`/api/fee/invoices/${invoiceId}/installments`, {
        installment_count: Number(instForm.installment_count) || 1,
        installment_start: instForm.installment_start || null
      });
      setShowInst(false);
      fetchInvoice();
    } catch (err) {
      setError(err.response?.data?.message || 'แก้ไขงวดผ่อนไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('ยกเลิกใบแจ้งหนี้นี้?')) return;
    try {
      await axiosInstance.put(`/api/fee/invoices/${invoiceId}/cancel`);
      fetchInvoice();
    } catch (err) {
      setError(err.response?.data?.message || 'ยกเลิกไม่สำเร็จ');
    }
  };

  const handlePrint = () => {
    if (!invoice) return;
    const itemsHtml = invoice.items.map((it) => `
      <tr>
        <td style="border:1px solid #ccc;padding:6px;">${it.item_name_snapshot}</td>
        <td style="border:1px solid #ccc;padding:6px;text-align:center;">${STREAM[it.stream] || it.stream}</td>
        <td style="border:1px solid #ccc;padding:6px;text-align:right;">${baht(it.amount)}</td>
      </tr>`).join('');
    const payHtml = invoice.payments.map((p) => `
      <tr>
        <td style="border:1px solid #ccc;padding:6px;">${new Date(p.pay_date).toLocaleDateString('th-TH')}</td>
        <td style="border:1px solid #ccc;padding:6px;">${METHOD[p.pay_method] || p.pay_method}</td>
        <td style="border:1px solid #ccc;padding:6px;text-align:right;">${baht(p.pay_amount)}</td>
      </tr>`).join('');

    // ตารางงวดต้องอยู่บนใบที่ผู้ปกครองถือกลับไป ไม่งั้นเขาไม่รู้ว่าต้องจ่ายเท่าไรเดือนไหน
    const instHtml = (invoice.installments || []).map((it) => `
      <tr>
        <td style="border:1px solid #ccc;padding:6px;">${it.inst_label || `งวดที่ ${it.inst_no}`}</td>
        <td style="border:1px solid #ccc;padding:6px;">${thaiDate(it.due_date)}</td>
        <td style="border:1px solid #ccc;padding:6px;text-align:right;">${baht(it.amount)}</td>
        <td style="border:1px solid #ccc;padding:6px;text-align:right;">${baht(it.outstanding)}</td>
      </tr>`).join('');

    const w = window.open('', '_blank', 'width=800,height=900');
    w.document.write(`
      <html><head><title>${invoice.invoice_no}</title>
      <style>body{font-family:Tahoma,Arial,sans-serif;color:#111;padding:24px;}
      table{border-collapse:collapse;width:100%;margin-top:8px;}
      th{border:1px solid #ccc;padding:6px;background:#f1f3f5;text-align:left;}</style>
      </head><body>
      <h2 style="margin:0 0 4px;">ใบแจ้งหนี้ / ใบเสร็จรับเงิน</h2>
      <div>โรงเรียนวัฒนธรรมอิสลาม</div>
      <hr/>
      <div><strong>เลขที่บิล:</strong> ${invoice.invoice_no}</div>
      <div><strong>นักเรียน:</strong> ${invoice.st_name} (${invoice.st_idnumber})</div>
      <div><strong>ปีการศึกษา/ภาคเรียน:</strong> ${invoice.year_year} / ${invoice.term_semester}</div>
      <div><strong>สถานะ:</strong> ${STATUS[invoice.invoice_status]?.label || invoice.invoice_status}</div>
      <h3 style="margin:16px 0 4px;">รายการค่าใช้จ่าย</h3>
      <table><thead><tr><th>รายการ</th><th style="text-align:center;">สาย</th><th style="text-align:right;">จำนวนเงิน</th></tr></thead>
      <tbody>${itemsHtml}</tbody></table>
      <div style="text-align:right;margin-top:8px;"><strong>รวมทั้งสิ้น: ${baht(invoice.total_amount)} บาท</strong></div>
      <div style="text-align:right;">ชำระแล้ว: ${baht(invoice.paid_amount)} บาท</div>
      <div style="text-align:right;"><strong>คงค้าง: ${baht(invoice.balance)} บาท</strong></div>
      ${(invoice.installments || []).length > 1 ? `<h3 style="margin:16px 0 4px;">กำหนดชำระรายงวด</h3>
      <table><thead><tr><th>งวด</th><th>ครบกำหนด</th><th style="text-align:right;">ยอดงวด</th><th style="text-align:right;">คงเหลือ</th></tr></thead>
      <tbody>${instHtml}</tbody></table>` : ''}
      ${invoice.payments.length ? `<h3 style="margin:16px 0 4px;">ประวัติการชำระ</h3>
      <table><thead><tr><th>วันที่</th><th>ช่องทาง</th><th style="text-align:right;">จำนวนเงิน</th></tr></thead>
      <tbody>${payHtml}</tbody></table>` : ''}
      <p style="margin-top:32px;">ออกเมื่อ ${new Date().toLocaleString('th-TH')}</p>
      </body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  if (loading) {
    return (
      <React.Fragment>
        <Header />
        <main className="main main-app p-3 p-lg-4">
          <Container fluid className="px-0 text-center py-5"><Spinner animation="border" /></Container>
        </main>
      </React.Fragment>
    );
  }

  if (!invoice) {
    return (
      <React.Fragment>
        <Header />
        <main className="main main-app p-3 p-lg-4">
          <Container fluid className="px-0">
            <Alert variant="danger">{error || 'ไม่พบใบแจ้งหนี้'}</Alert>
            <Button variant="secondary" onClick={() => navigate('/fee/invoices')}>กลับ</Button>
          </Container>
        </main>
      </React.Fragment>
    );
  }

  const isCancelled = invoice.invoice_status === 'cancelled';
  const isPaid = invoice.invoice_status === 'paid';

  return (
    <React.Fragment>
      <Header />
      <main className="main main-app p-3 p-lg-4">
        <Container fluid className="px-0">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div>
              <Button variant="link" className="p-0 mb-1" onClick={() => navigate('/fee/invoices')}>
                <i className="ri-arrow-left-line me-1" />กลับรายการบิล
              </Button>
              <h2 className="mb-0">{invoice.invoice_no}</h2>
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" onClick={handlePrint}>
                <i className="ri-printer-line me-1" />พิมพ์
              </Button>
              {!isCancelled && !isPaid && (
                <Button variant="primary" onClick={openPay}>
                  <i className="ri-money-dollar-circle-line me-1" />รับชำระ
                </Button>
              )}
            </div>
          </div>

          {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

          <Row className="g-3 mb-3">
            <Col lg={4}>
              <Card className="border-0 shadow-sm h-100"><Card.Body>
                <h6 className="text-muted mb-2">ข้อมูลนักเรียน</h6>
                <div className="fw-medium">{invoice.st_name}</div>
                <div className="text-muted">{invoice.st_idnumber}</div>
                <hr />
                <div className="d-flex justify-content-between"><span className="text-muted">ปี/ภาค</span><span>{invoice.year_year} / {invoice.term_semester}</span></div>
                <div className="d-flex justify-content-between"><span className="text-muted">สถานะ</span>
                  <Badge bg={STATUS[invoice.invoice_status]?.bg}>{STATUS[invoice.invoice_status]?.label}</Badge>
                </div>
              </Card.Body></Card>
            </Col>
            <Col lg={8}>
              <Card className="border-0 shadow-sm h-100"><Card.Body>
                <Row className="text-center">
                  <Col><small className="text-muted d-block">ยอดรวม</small><h4 className="mb-0">{baht(invoice.total_amount)}</h4></Col>
                  <Col><small className="text-muted d-block">ชำระแล้ว</small><h4 className="mb-0 text-success">{baht(invoice.paid_amount)}</h4></Col>
                  <Col><small className="text-muted d-block">คงค้าง</small><h4 className="mb-0 text-danger">{baht(invoice.balance)}</h4></Col>
                </Row>
                {!isCancelled && !isPaid && invoice.payments.length === 0 && (
                  <div className="text-end mt-3">
                    <Button size="sm" variant="outline-danger" onClick={handleCancel}>ยกเลิกบิล</Button>
                  </div>
                )}
              </Card.Body></Card>
            </Col>
          </Row>

          <Card className="border-0 shadow-sm mb-3">
            <Card.Header className="bg-white"><strong>รายการค่าใช้จ่าย</strong></Card.Header>
            <Card.Body className="p-0">
              <Table className="mb-0">
                <thead className="table-light">
                  <tr><th>รายการ</th><th className="text-center">สาย</th><th className="text-end">จำนวนเงิน</th></tr>
                </thead>
                <tbody>
                  {invoice.items.map((it) => (
                    <tr key={it.inv_item_id}>
                      <td>
                        {it.item_name_snapshot}
                        {it.source === 'dorm' && <Badge bg="info" className="ms-2">ค่าหอพัก</Badge>}
                      </td>
                      <td className="text-center"><Badge bg="light" text="dark">{STREAM[it.stream] || it.stream}</Badge></td>
                      <td className="text-end">{baht(it.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="table-light"><th colSpan={2}>รวม</th><th className="text-end">{baht(invoice.total_amount)}</th></tr>
                </tfoot>
              </Table>
            </Card.Body>
          </Card>

          {/* งวดผ่อนรายเดือน — บิลยังเป็นใบเดียว ตารางนี้บอกแค่ว่าต้องจ่ายเท่าไรเมื่อไร
              สถานะรายงวดคิดจากยอดที่ชำระแล้ว ไล่ลงงวดที่ 1, 2, 3 ตามลำดับ */}
          <Card className="border-0 shadow-sm mb-3">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center">
              <strong>งวดผ่อนชำระ</strong>
              {!isCancelled && (
                <Button size="sm" variant="outline-secondary" onClick={openInst}>
                  <i className="ri-calendar-schedule-line me-1" />
                  {invoice.installments?.length > 1 ? 'แก้ไขงวด' : 'แบ่งชำระ'}
                </Button>
              )}
            </Card.Header>
            <Card.Body className="p-0">
              {!invoice.installments || invoice.installments.length <= 1 ? (
                <div className="text-center text-muted py-4">
                  บิลนี้ชำระครั้งเดียว ไม่ได้แบ่งงวด
                </div>
              ) : (
                <Table className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>งวด</th><th>ครบกำหนด</th>
                      <th className="text-end">ยอดงวด</th>
                      <th className="text-end">ชำระแล้ว</th>
                      <th className="text-end">คงเหลือ</th>
                      <th className="text-center">สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.installments.map((it) => (
                      <tr key={it.inst_id} className={it.overdue ? 'table-danger' : ''}>
                        <td>{it.inst_label || `งวดที่ ${it.inst_no}`}</td>
                        <td>
                          {thaiDate(it.due_date)}
                          {it.overdue && <Badge bg="danger" className="ms-2">เกินกำหนด</Badge>}
                        </td>
                        <td className="text-end">{baht(it.amount)}</td>
                        <td className="text-end text-success">{baht(it.paid_amount)}</td>
                        <td className="text-end">{baht(it.outstanding)}</td>
                        <td className="text-center">
                          <Badge bg={INST_STATUS[it.inst_status]?.bg}>
                            {INST_STATUS[it.inst_status]?.label}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white"><strong>ประวัติการชำระ</strong></Card.Header>
            <Card.Body className="p-0">
              {invoice.payments.length === 0 ? (
                <div className="text-center text-muted py-4">ยังไม่มีการชำระ</div>
              ) : (
                <Table className="mb-0">
                  <thead className="table-light">
                    <tr><th>เลขที่</th><th>วันที่</th><th>ช่องทาง</th><th>เลขใบเสร็จ</th><th>ผู้รับเงิน</th><th className="text-end">จำนวนเงิน</th></tr>
                  </thead>
                  <tbody>
                    {invoice.payments.map((p) => (
                      <tr key={p.payment_id}>
                        <td>{p.payment_no}</td>
                        <td>{new Date(p.pay_date).toLocaleDateString('th-TH')}</td>
                        <td>{METHOD[p.pay_method] || p.pay_method}</td>
                        <td>{p.receipt_no || '-'}</td>
                        <td>{p.received_by || '-'}</td>
                        <td className="text-end">{baht(p.pay_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Container>
      </main>

      <Modal show={showPay} onHide={() => setShowPay(false)} centered>
        <Modal.Header closeButton><Modal.Title>รับชำระเงิน</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="mb-3 p-2 bg-light rounded">
            ยอดคงค้าง: <strong className="text-danger">{baht(invoice.balance)}</strong> บาท
          </div>
          <Row className="g-3">
            <Col xs={6}>
              <Form.Label>จำนวนเงิน (บาท)</Form.Label>
              <Form.Control type="number" min="0" max={invoice.balance}
                value={payForm.pay_amount}
                onChange={(e) => setPayForm({ ...payForm, pay_amount: e.target.value })} />
            </Col>
            <Col xs={6}>
              <Form.Label>วันที่ชำระ</Form.Label>
              <ThaiDateInput value={payForm.pay_date}
                onChange={v => setPayForm({ ...payForm, pay_date: v })} />
            </Col>
            <Col xs={6}>
              <Form.Label>ช่องทาง</Form.Label>
              <Form.Select value={payForm.pay_method}
                onChange={(e) => setPayForm({ ...payForm, pay_method: e.target.value })}>
                <option value="cash">เงินสด</option>
                <option value="transfer">โอน</option>
              </Form.Select>
            </Col>
            <Col xs={6}>
              <Form.Label>เลขใบเสร็จ (ไม่บังคับ)</Form.Label>
              <Form.Control value={payForm.receipt_no}
                onChange={(e) => setPayForm({ ...payForm, receipt_no: e.target.value })} />
            </Col>
            <Col xs={12}>
              <Form.Label>หมายเหตุ</Form.Label>
              <Form.Control as="textarea" rows={2} value={payForm.payment_note}
                onChange={(e) => setPayForm({ ...payForm, payment_note: e.target.value })} />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPay(false)}>ยกเลิก</Button>
          <Button variant="primary" onClick={handlePay} disabled={saving}>
            {saving ? 'กำลังบันทึก...' : 'บันทึกการชำระ'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showInst} onHide={() => setShowInst(false)} centered>
        <Modal.Header closeButton><Modal.Title>แบ่งชำระรายเดือน</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="mb-3 p-2 bg-light rounded">
            ยอดบิล: <strong>{baht(invoice.total_amount)}</strong> บาท — จะถูกแบ่งตามจำนวนงวดที่เลือก
          </div>
          <Row className="g-3">
            <Col xs={6}>
              <Form.Label>จำนวนงวด</Form.Label>
              <Form.Select
                value={instForm.installment_count}
                onChange={(e) => setInstForm({ ...instForm, installment_count: e.target.value })}
              >
                <option value={1}>จ่ายครั้งเดียว</option>
                {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                  <option key={n} value={n}>{n} งวด</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6}>
              <Form.Label>ครบกำหนดงวดแรก</Form.Label>
              <ThaiDateInput
                value={instForm.installment_start}
                onChange={v => setInstForm({ ...instForm, installment_start: v })}
              />
            </Col>
            <Col xs={12}>
              <small className="text-muted">
                งวดถัดไปครบกำหนดเดือนละครั้ง · เศษสตางค์รวมอยู่ที่งวดสุดท้ายเพื่อให้ผลรวมเท่ากับยอดบิลพอดี
                · การรับชำระยังทำที่บิลตามปกติ ไม่ต้องเลือกงวด
              </small>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInst(false)}>ยกเลิก</Button>
          <Button variant="primary" onClick={handleInst} disabled={saving}>
            {saving ? 'กำลังบันทึก...' : 'บันทึก'}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default FeeInvoiceDetail;
